import type { Shift } from '@keetcheck/shared';
import { useShifts } from './queries.js';
import { addDays, localDay } from './shifts.js';

const ORDER: Shift['kind'][] = ['udo', 'udo_day', 'duty_crew', 'ops_support', 'activity'];
const byKind = (a: Shift, b: Shift) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind);

/**
 * Who's on right now, and the next change: shifts covering this moment, and
 * the group of shifts that start next. Fetches yesterday → the day after
 * tomorrow so a night that started yesterday is included.
 */
export function useOnNow(orgId: string, now = new Date()) {
  const from = localDay(new Date(now.getTime() - 86400_000).toISOString());
  const to = addDays(localDay(now.toISOString()), 2);
  const q = useShifts(orgId, from, to);
  const shifts = q.data ?? [];
  const t = now.getTime();
  const current = shifts.filter((s) => new Date(s.startsAt).getTime() <= t && new Date(s.endsAt).getTime() > t).sort(byKind);
  const upcoming = shifts.filter((s) => new Date(s.startsAt).getTime() > t);
  const nextStart = upcoming.length ? Math.min(...upcoming.map((s) => new Date(s.startsAt).getTime())) : null;
  const next = nextStart === null ? [] : upcoming.filter((s) => new Date(s.startsAt).getTime() === nextStart).sort(byKind);
  return { ...q, current, next, nextStart };
}

/** Plain-words summary of a shift's person or crew, for compact cards. */
export function whoLabel(s: Shift): string {
  if (s.userId) {
    const taker = s.cover?.takers.find((x) => new Date(x.startsAt) <= new Date() && new Date(x.endsAt) > new Date());
    return taker ? `${taker.displayName} (for ${s.userName})` : s.cover?.gaps.length ? `${s.userName} — gap` : s.userName ?? '';
  }
  if (s.kind === 'duty_crew') return s.teamName ? `${s.teamName}${s.capability ? ` · ${s.capability.count} on` : ''}` : 'Scratch crew';
  if (s.kind === 'ops_support') return s.teamName ? `${s.teamName} · not picked` : 'Not picked';
  if (s.kind === 'udo' || s.kind === 'udo_day') return 'Not picked';
  return '';
}
